import { AfterViewInit, Component, Inject, Input, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { IStateParamsService, IStateService } from 'angular-ui-router';
import { compact as _compact, map as _map, reduce as _reduce } from 'lodash';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { SpinnerModal } from '../../../../app/shared/modals/status-spinner/status-spinner.service';
import { IStudent } from '../../../shared/typings/interfaces/student.interface';

// Note: to change sorted order of autocomplete, add column to sort on to `DEFAULT_SORT` (CM).

interface IStudentsDisplayTextHash {
  [key: string]: string;
}

@Component({
  selector: 'student-search',
  template: require('./student-search.component.html'),
  styles: [require('./student-search.component.scss')],
})
export class StudentSearchComponent implements OnInit, AfterViewInit {
  @Input()
  students: any[]; // Flattened students
  student; // Flattened current student

  @ViewChild(MatAutocompleteTrigger)
  trigger;

  studentSearchControl: FormControl = new FormControl();
  filteredStudents: Observable<IStudent[]>;
  studentsDisplayTextHash: IStudentsDisplayTextHash = {};
  initialSearch: boolean = true;

  constructor(
    @Inject('$stateParams') private $stateParams: IStateParamsService,
    @Inject('$state') private $state: IStateService,
    @Inject('SpinnerModal') private spinnerModal: SpinnerModal,
  ) {
    //
  }

  public ngOnInit(): void {
    const { studentId } = this.$stateParams;
    this.student = this.getCurrentStudent(this.students, studentId);
    this.studentsDisplayTextHash = this.createStudentsDisplayTextHash(this.students);

    // sets autocomplete value to display current student (CM).
    this.studentSearchControl.setValue(this.student._id);

    this.filteredStudents = this.studentSearchControl.valueChanges.pipe(
      startWith(''),
      map(val => this.filter(val)),
    );
  }

  public ngAfterViewInit(): void {
    this.trigger.panelClosingActions.subscribe(e => {
      if (!this.studentSearchControl.value) this.studentSearchControl.setValue(this.student._id);
    });
  }

  public onFocus(): void {
    // format student names to be searchable only on initial autocomplete search (CM).
    if (this.initialSearch) {
      this.studentsDisplayTextHash = this.createStudentsDisplayTextHash(this.students);

      this.initialSearch = false;
    }

    if (this.studentSearchControl.value === this.student._id) this.studentSearchControl.setValue('');
  }

  // in this context, students is flattenedStudents (CM).
  private getCurrentStudent(students: any[], studentId: string) {
    const currentStudent = students.find(student => student._id === studentId);

    return currentStudent;
  }

  private filter(val: string): IStudent[] {
    if (val) {
      let searchTerms = _compact(val.replace(',', ' ').split(' '));
      searchTerms = _map(searchTerms, (term: any) => term.toLowerCase());

      const filteredStudents = this.students.filter(student => {
        return searchTerms.every(term => {
          const { _id } = student;
          const studentName = this.studentsDisplayTextHash[_id];

          return studentName.toLowerCase().includes(term) || student.studentId.includes(term);
        });
      });

      return filteredStudents;
    }

    return this.students;
  }

  public getStudentDisplayText(studentsDisplayTextHash, val) {
    return studentsDisplayTextHash[val];
  }

  public goToStudent(event: MatAutocompleteSelectedEvent): void {
    this.spinnerModal.activateSpinner(`Loading ${event.option.viewValue}'s profile`);
    const _id = event.option.value;
    const { _id: currentStudentId } = this.student;
    const studentIdIsTheSame = currentStudentId === _id;

    if (!studentIdIsTheSame) {
      this.$state.go('main.school.single-student', {
        filter: this.$state.params.filter,
        studentId: _id,
      });
    } else {
      // sets autocomplete value to display current student (CM).
      this.studentSearchControl.setValue(this.student._id);
    }
  }

  private createStudentDisplayText(student): string {
    const { firstName, lastName } = student;
    const studentDisplayText = `${firstName} ${lastName}`;

    return studentDisplayText;
  }

  private createStudentsDisplayTextHash(students: IStudent[]) {
    const studentsDisplayTextHash = _reduce(
      students,
      (result, student) => {
        const studentName = this.createStudentDisplayText(student);

        result[student._id] = studentName;

        return result;
      },
      {},
    );

    return studentsDisplayTextHash;
  }
}
